import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

import { getCurrentInstance, computed } from 'vue';
import CalendarDayItem from './CalendarDayItem.vue';
import CalendarCardItem from './CalendarCardItem.vue';
import endOfDay from 'date-fns/endOfDay';
import isWeekend from 'date-fns/isWeekend';
import addDays from 'date-fns/addDays';
import startOfWeek from 'date-fns/startOfWeek';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import startOfDay from 'date-fns/startOfDay';
import getTime from 'date-fns/getTime';
import isToday from 'date-fns/isToday';
import isPast from 'date-fns/isPast';
import isFuture from 'date-fns/isFuture';
import isThisMonth from 'date-fns/isThisMonth';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';


export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarWeekItem',
  props: {
  // timestamp of the week
  id: {
    type: Number,
    required: true,
  },
  weekItem: {
    type: Object,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  maxCardsVisible: {
    type: Number,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const _this = getCurrentInstance()!.proxy as any;

const sortByColumn = (a: any, b: any) => {
  return b.layout.column.span - a.layout.column.span;
};

const isOverlapping = (
  layout: { row: { start: number; span: number }; column: { start: number; span: number } },
  cardList: any
) => {
  return cardList.some(
    (card: any) =>
      card.layout.row.start < layout.row.start + layout.row.span &&
      card.layout.row.start + card.layout.row.span > layout.row.start &&
      card.layout.column.start < layout.column.start + layout.column.span &&
      card.layout.column.start + card.layout.column.span > layout.column.start
  );
};

const shiftCardDown = (layout: any, cardList: any) => {
  let newLayout = layout;
  while (isOverlapping(newLayout, cardList)) {
    newLayout = {
      ...newLayout,
      row: {
        ...newLayout.row,
        start: newLayout.row.start + 1,
      },
    };
  }
  return newLayout;
};

const applyGridStyle = (resource: any) => {
  return {
    ...resource,
    style: {
      gridColumn: `${resource.layout.column.start} / span ${resource.layout.column.span}`,
      gridRow: `${resource.layout.row.start} / span ${resource.layout.row.span}`,
    },
  };
};

const showWeekend = computed(() => _this.$store.getters.getShowWeekends);
const numberOfDays = computed(() => (showWeekend.value ? 7 : 5));

const getWeekDay = (day: number) => {
  // 0 based 0 sunday, it should be 1 based 7 sunday
  return day === 0 ? numberOfDays.value : day;
};

const dayItems = computed(() => {
  const start = startOfWeek(props.id, { weekStartsOn: 1 });
  const end = endOfDay(addDays(props.id, numberOfDays.value - 1));

  return eachDayOfInterval({ start, end }).map((day: Date) => {
    const startOfCurrentDay = startOfDay(day);
    const startOfCurrentDayTimestamp = getTime(startOfCurrentDay);
    const isTodayDate = isToday(day);
    const isPastDate = isTodayDate ? false : isPast(startOfCurrentDay);
    const isFutureDate = isTodayDate ? false : isFuture(startOfCurrentDay);
    const weekDay = getDay(day);
    const weekDayConverted = getWeekDay(weekDay);

    return {
      dayTimestamp: startOfCurrentDayTimestamp,
      day: format(day, 'd', { weekStartsOn: 1 }),
      isToday: isTodayDate,
      isCurrentMonth: isThisMonth(day),
      isPast: isPastDate,
      isFuture: isFutureDate,
      isWeekend: isWeekend(day),
      showMoreCardsCount:
        processedWeekCards.value.showMoreCountByColumnStart[weekDayConverted]?.length ?? 0,
      moreCards: processedWeekCards.value.showMoreCountByColumnStart[weekDayConverted] ?? [],
    };
  });
});

const weekCards = computed(() => {
  return props.weekItem.weekCards;
});

const sortedWeekCards = computed(() => {
  const sortedCards = [...weekCards.value].sort(sortByColumn);
  return sortedCards.reduce(
    (acc: any, card: any) => [
      ...acc,
      {
        ...card,
        layout: shiftCardDown(card.layout, acc),
      },
    ],
    []
  );
});

// Aggregate column spans and counts
const aggregateColumnSpans = (card: any, initial: any) => {
  return Array.from(
    { length: card.layout.column.span },
    (_, i) => card.layout.column.start + i
  ).reduce(
    (acc, column) => ({
      ...acc,
      [column]: [...(acc[column] ?? []), card],
    }),
    initial
  );
};

const processedWeekCards = computed(() => {
  return sortedWeekCards.value.reduce(
    (acc: any, card: any) => {
      if (card.layout.row.start - 1 > props.maxCardsVisible) {
        return {
          ...acc,
          showMoreCountByColumnStart: aggregateColumnSpans(card, acc.showMoreCountByColumnStart),
        };
      }
      return {
        ...acc,
        cards: [...acc.cards, card],
      };
    },
    { cards: [], showMoreCountByColumnStart: {} }
  );
});

const cardItems = computed(() => {
  return processedWeekCards.value.cards.map((card: any) => {
    return {
      ...card,
      ...applyGridStyle(card),
    };
  });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["calendar-week-item-wrap", { 'grid-cols-7': showWeekend.value }]),
    style: _normalizeStyle({ height: `${__props.height}px` })
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dayItems.value, (day, index) => {
      return (_openBlock(), _createBlock(CalendarDayItem, {
        key: day.dayTimestamp,
        "day-index": index,
        "date-timestamp": day.dayTimestamp,
        "day-humanized": day.day,
        "is-today": day.isToday,
        "is-current-month": day.isCurrentMonth,
        "is-past": day.isPast,
        "is-future": day.isFuture,
        "day-is-weekend": day.isWeekend,
        "max-cards-visible": __props.maxCardsVisible,
        "show-more-cards-count": day.showMoreCardsCount,
        "more-cards": day.moreCards,
        "week-id": __props.id
      }, null, 8, ["day-index", "date-timestamp", "day-humanized", "is-today", "is-current-month", "is-past", "is-future", "day-is-weekend", "max-cards-visible", "show-more-cards-count", "more-cards", "week-id"]))
    }), 128)),
    _cache[0] || (_cache[0] = _createTextVNode()),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cardItems.value, (card, index) => {
      return (_openBlock(), _createBlock(CalendarCardItem, {
        key: index,
        card: card,
        "week-id": __props.id,
        index: index,
        "max-cards-visible": __props.maxCardsVisible
      }, null, 8, ["card", "week-id", "index", "max-cards-visible"]))
    }), 128))
  ], 6))
}
}

})